import { createContext, memo, useContext, useState } from "react";
import { Input, Collapse } from "antd";
import { AiOutlineSend } from "react-icons/ai";

import styles from "./Chats.module.scss";
import ChatList from "./chat-list";
import { useUser } from "../../../hook";
import { IChat } from "./chat-item";
import { DateHelper, StringHelper } from "../../../helper";
import { UseMutateFunction } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

interface Props {
  items?: IChat[];
  requestId?: string;
  initialValues?: IChat[];
  mutateFn?: UseMutateFunction<AxiosResponse<any>, unknown, { id: string; body: any }, unknown>;
  language?: "vi" | "en";
  onChange?: (chatList: IChat[]) => void;
}

export const ChatContext = createContext<{
  items: IChat[];
  handleAddChat: (id: string, content: string) => void;
  handleUpdateChat: (id: string, newItem: IChat) => void;
}>({ items: [], handleAddChat: () => {}, handleUpdateChat: () => {} });

const addChat = (parentId: string | null, newChat: IChat, chatList: IChat[] = []) => {
  if (parentId === null || chatList.length === 0) {
    chatList.push(newChat);
    return chatList;
  }
  const addChatToNode = (node: any) => {
    if (node.id === parentId) {
      node.children.push(newChat);
      return true;
    }
    for (const child of node.children) {
      if (addChatToNode(child)) {
        return true;
      }
    }
    return false;
  };
  const updatedChatList = chatList.map((node) => ({ ...node }));

  for (const child of updatedChatList) {
    addChatToNode(child);
  }

  return updatedChatList;
};

export const ChatsV2 = memo(
  ({
    items = [],
    requestId = "",
    initialValues = [],
    mutateFn = (variables: any) => {},
    language = "vi",
    onChange = () => {},
  }: Partial<Props>) => {
    const [chats, setChats] = useState<IChat[]>(items);
    const [rootChatValue, setRootChatValue] = useState("");
    const [activeKey, setActiveKey] = useState<string[] | string>(["1"]);

    const { user } = useUser();

    const isCollapsed = activeKey.length === 0;

    const handleOnChange = (updatedChats: IChat[]) => {
      setChats(updatedChats);
      mutateFn({
        id: requestId,
        body: { eComments: JSON.stringify(updatedChats) },
      });
    };

    const handleAddRootChat = () => {
      if (rootChatValue.trim()) {
        const newChat = {
          id: StringHelper.random(),
          author: user.current.name,
          datetime: DateHelper.getIsoStrNow(),
          content: rootChatValue,
          like: [],
          dislike: [],
          children: [],
        };
        const newChatList = addChat(null, newChat, chats);
        handleOnChange(newChatList);
        setRootChatValue("");
      }
    };

    const handleAddChat = (parentId: string, content: string) => {
      const newChat = {
        id: StringHelper.random(),
        author: user.current.name,
        datetime: DateHelper.getIsoStrNow(),
        content,
        like: [],
        dislike: [],
        children: [],
      };
      const newChatList = addChat(parentId, newChat, chats);
      handleOnChange(newChatList);
    };

    const handleUpdateChat = (idChat: string, newChat: any) => {
      const updateNestedItem = (items: any[] = []): any[] => {
        return items.map((item: any) => {
          if (item.id === idChat) {
            return { ...item, ...newChat };
          } else if (item.children && item.children.length > 0) {
            return { ...item, children: updateNestedItem(item.children) };
          }
          return item;
        });
      };
      const updatedList = updateNestedItem(chats);
      handleOnChange(updatedList);
    };

    const countItems = (items: any[]): number => {
      let count = 0;
      const countNestedItems = (nestedItems: any[]) => {
        for (const item of nestedItems) {
          count++;
          if (item.children && item.children.length > 0) {
            countNestedItems(item.children);
          }
        }
      };
      countNestedItems(chats);
      return count;
    };

    return (
      <ChatContext.Provider value={{ items: chats, handleAddChat, handleUpdateChat }}>
        <div className={`${styles.container} ${styles["container--outless"]}`}>
          <div className={styles.title}>
            {countItems(chats) === 0 ? "Chưa có thông tin nào được trao đổi" : `${countItems(chats)} Thông tin`}
          </div>
          {Boolean(chats.length) && (
            <Collapse
              ghost
              activeKey={activeKey}
              defaultActiveKey={["1"]}
              onChange={(keys: string[] | string) => setActiveKey(keys)}
            >
              <Collapse.Panel key="1" header={isCollapsed ? "Hiện thông tin" : "Ẩn thông tin"}>
                <ChatList items={chats} />
              </Collapse.Panel>
            </Collapse>
          )}

          <Input
            value={rootChatValue}
            onChange={(e) => setRootChatValue(e.target.value)}
            className={styles.commentInput}
            size="large"
            suffix={<AiOutlineSend onClick={handleAddRootChat} style={{ cursor: "pointer" }} />}
            placeholder="Nhập thông tin trao đổi (nếu có)"
            onPressEnter={handleAddRootChat}
          />
        </div>
      </ChatContext.Provider>
    );
  },
);

export const useChat = () => {
  const context = useContext<{
    items: any[];
    handleAddChat: (id: string, content: string) => void;
    handleUpdateChat: (id: string, newItem: any) => void;
  }>(ChatContext);
  if (context === undefined) throw new Error("ChatContext was used outside of ChatProvider");
  return context;
};

export * from "./chat-item";
